// src/components/ProfileCropper.js
import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { ArrowLeft, Check, ZoomIn, Move } from 'lucide-react';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import styles from './ProfileCropper.module.css';

const ProfileCropper = ({ image, onComplete, onCancel, userId }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.5); // Start with slightly zoomed in
  const [loading, setLoading] = useState(false);
  
  // This function generates a cropped image blob
  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    
    // Create a canvas to draw the cropped image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions to the final desired size (1:1 aspect ratio)
    canvas.width = 400;  // Fixed width for profile pic
    canvas.height = 400; // Fixed height for profile pic
    
    // Wait for image to load
    await new Promise((resolve) => {
      image.onload = resolve;
    });
    
    // Calculate the scale factor between natural dimensions and displayed dimensions
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Calculate dimensions of the source image
    const sourceWidth = pixelCrop.width * scaleX;
    const sourceHeight = pixelCrop.height * scaleY;
    const sourceX = pixelCrop.x * scaleX;
    const sourceY = pixelCrop.y * scaleY;
    
    // Draw the cropped image
    ctx.drawImage(
      image,
      sourceX, sourceY,
      sourceWidth, sourceHeight,
      0, 0,
      canvas.width, canvas.height
    );
    
    // Get the data as blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95); // JPEG at 95% quality
    });
  };
  
  const onCropComplete = useCallback(async (_, croppedAreaPixels) => {
    try {
      // Store the crop details for later when "Save" is clicked
      window.latestCrop = croppedAreaPixels;
    } catch (e) {
      console.error('Error getting cropped image:', e);
    }
  }, []);
  
  const handleSave = async () => {
    if (!window.latestCrop || !image || !userId) return;
    
    setLoading(true);
    
    try {
      // Create blob from cropped image
      const croppedImage = await createCroppedImage(image, window.latestCrop);
      
      // Create a file from the blob
      const file = new File([croppedImage], 'profile.jpg', { type: 'image/jpeg' });
      
      // Upload to Firebase storage
      const fileName = `profile-pics/${userId}_${Date.now()}.jpg`;
      const storageRef = ref(storage, fileName);
      const uploadResult = await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(uploadResult.ref);
      
      // Update Firestore
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, { photoURL });
      
      // Complete successfully
      onComplete(photoURL);
    } catch (error) {
      console.error('Error saving cropped image:', error);
      alert('Error saving profile picture: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate a good crop area size based on viewport
  const getCropSize = () => {
    // On mobile, use a smaller crop area
    if (window.innerWidth < 768) {
      return Math.min(window.innerWidth * 0.7, window.innerHeight * 0.35);
    }
    
    // On desktop, use a medium-sized crop area
    return Math.min(window.innerWidth * 0.3, window.innerHeight * 0.4);
  };
  
  return (
    <div className={styles.cropperContainer}>
      <div className={styles.cropperHeader}>
        <button className={styles.backButton} onClick={onCancel}>
          <ArrowLeft size={20} />
          <span>Cancel</span>
        </button>
        <h3>Position and Size</h3>
        <button 
          className={styles.saveButton} 
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : (
            <>
              <Check size={20} />
              <span>Save</span>
            </>
          )}
        </button>
      </div>
      
      <div className={styles.cropArea}>
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={1} // Maintain 1:1 aspect ratio for circular profile pic
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          cropSize={{ width: getCropSize(), height: getCropSize() }} // Consistent size
          minZoom={0.8}
          maxZoom={5}
          disableAutomaticStylesInjection={false} // Let the library inject its styles
        />
      </div>
      
      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <ZoomIn size={20} />
          <input
            type="range"
            min={0.8}
            max={5}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className={styles.slider}
          />
          <span className={styles.zoomLabel}>Zoom</span>
        </div>
        
        <div className={styles.instruction}>
          <Move size={18} />
          <span>Drag to reposition</span>
        </div>
      </div>
    </div>
  );
};

export default ProfileCropper;